import { BaseFilters } from "../../../components/filters/FilterToolBar";
import { api, createURLSearchParams } from "../api";
import axios, { BaseItem } from "../axios";
import { UserRole } from "./user/credentials";

export enum TriageStatus {
  UNDER_REVIEW = "underReview",
  APPROVED = "approved",
  REJECTED = "rejected",
  NOT_OPTED = "triage-not-opted"
}

export type UserAPIResponse = {
  user_id: number;
  email: string;
  username: string;
  role: UserRole;
  two_fa_enabled: boolean;
  is_approved: boolean;
  first_time_uxp: boolean;
  email_verified: boolean;
  verification_status: string;
  triage_status: string;
  about: string | null;
  country: string | null;
  pfp: string | null;
  display_name: string | null;
  links: string | null;
  skills: string | null;
  banner: string | null;
  email_notifications_enabled: boolean;
  sow_exempt?: boolean;
  totp_confirmed_at: string | null;
};

export type UserQueryFilters = {
  page?: string;
  limit?: string;
  roles?: string;
  ids?: string;
  active?: string;
  username?: string;
  companyIds?: string;
  mfa?: string;
};

export type UserFilters = {
  page?: number;
  roles?: UserRole[];
  ids?: number[];
  active?: ("Active" | "Inactive")[];
  companyIds?: number[];
  mfa?: ("TOTP" | "Email" | "Disabled")[];
} & BaseFilters;

export type User = {
  email: string;
  username: string;
  role: UserRole;
  twoFaEnabled: boolean;
  approved: boolean;
  firstTimeUXP: boolean;
  emailVerified: boolean;
  verificationStatus: string;
  triageStatus: string;
  displayName?: string;
  about?: string;
  country?: string;
  links?: string[];
  skills?: string[];
  profilePicture?: string;
  bannerPicture?: string;
  email_notifications_enabled: boolean;
  sowExempt?: boolean;
  totpConfirmedAt?: string | null;
} & BaseItem;


type PaginatedUsers = {
  users: User[];
  count: number;
};

type PaginatedUsersResponse = {
  users: UserAPIResponse[];
  count: number;
};

const parseUserDetails = (user: UserAPIResponse) =>
  ({
    id: user.user_id,
    email: user.email,
    role: user.role,
    approved: user.is_approved,
    username: user.username,
    displayName: user.display_name ? user.display_name : undefined,
    about: user.about ? user.about : undefined,
    links: user.links ? JSON.parse(user.links) : undefined,
    skills: user.skills ? JSON.parse(user.skills) : undefined,
    country: user.country ? user.country : undefined,
    profilePicture: user.pfp ? user.pfp : undefined,
    bannerPicture: user.banner ? user.banner : undefined,
    email_notifications_enabled: user.email_notifications_enabled,
    sowExempt: user.sow_exempt,
    totpConfirmedAt: user.totp_confirmed_at,
    twoFaEnabled: user.two_fa_enabled
  }) as User;

const usersUrl = "/v2/user";

/**
 * Standalone get all users endpoint - used for inifite
 * scrolling when managing private access users
 */
export const getUsers = async ({
  page,
  limit,
  ids
}: {
  page: number;
  limit: number;
  ids: number[];
}) => {
  const response = (
    await axios.get(
      usersUrl + `/all?page=${page}&limit=${limit}&ids=${ids.join(",")}`
    )
  ).data as PaginatedUsersResponse;

  return {
    users: response.users.map(user => parseUserDetails(user)),
    count: response.count
  };
};

/**
 * The Manage Users API slice definition.
 *
 * These are the endpoint definitions for the user endpoints used
 * by admins when managing users - for regular user credetials and details
 * see relevant hooks
 */
export const userApi = api.injectEndpoints({
  endpoints: builder => ({
    /**
     * Retrieve all the users from the backend (for admin management)
     */
    getAllUsers: builder.query<PaginatedUsers, UserFilters>({
      query: filters => {
        const params = createURLSearchParams<UserQueryFilters>({
          page: filters?.page?.toString(),
          limit: filters?.limit.toString(),
          roles: filters?.roles?.join(","),
          ids: filters?.ids?.join(","),
          active: filters?.active?.map(a => a === "Active").join(","),
          username: filters?.search,
          companyIds: filters?.companyIds?.join(","),
          mfa: filters?.mfa?.map(m => m.toLowerCase()).join(",")
        });

        return {
          url: usersUrl + "/all?" + params.toString(),
          method: "get"
        };
      },
      transformResponse: (response: PaginatedUsersResponse) => ({
        users: response.users.map(parseUserDetails),
        count: response.count
      }),
      providesTags: ["ManageUsers"]
    }),
    /**
     * Activate/deactivate the given user
     */
    setUserActivation: builder.mutation<
      { success: boolean; message: string },
      { userId: number; isApproved?: boolean; sowExempt?: boolean }
    >({
      query: ({ userId, isApproved, sowExempt }) => ({
        url: usersUrl + "/status",
        method: "post",
        data: {
          id: userId,
          approved: isApproved,
          sow_exempt: sowExempt
        }
      }),
      invalidatesTags: ["ManageUsers"]
    }),
    /**
     * Send a polite reminder email to create a program (admin-only)
     */
    sendProgramReminder: builder.mutation<{ message: string }, { userId: number }>({
      query: ({ userId }) => ({
        url: usersUrl + "/reminder",
        method: "post",
        data: { id: userId }
      })
    }),
    /**
     * Update email notification preference for a user
     */
    updateUserNotifications: builder.mutation<
      { success: boolean; message: string },
      { userId?: number; status: boolean }
    >({
      query: ({ userId, status }) => ({
        url: usersUrl + "/notifications",
        method: "post",
        data: { userId, status }
      }),
      invalidatesTags: ["ManageUsers"]
    })
  })
});

export const {
  useGetAllUsersQuery,
  useSetUserActivationMutation,
  useSendProgramReminderMutation,
  useUpdateUserNotificationsMutation
} = userApi;

export type BusinessUserStats = {
  companyName: string | null;
  totalPrograms: number;
  activePrograms: number;
  totalUsers: number;
  totalReports: number;
  totalPentestReports: number;
};

/**
 * Fetch business-level stats for a business owner or sub-role user (admin-only)
 */
export const getBusinessUserStats = async (userId: number): Promise<BusinessUserStats> => {
  const response = await axios.get(usersUrl + `/business-stats?userId=${userId}`);
  return response.data as BusinessUserStats;
};
